'use client';
import styled from 'styled-components';

import { useBreakpoint } from '@contexts/breakpoints';
import Skeleton from '@components/skeletons/Skeleton';
import { Col, FlexibleDiv, Row } from '@components/formStyles';

export const ImagesSkeleton = styled.div`
  display: grid;
  grid-template-columns: minmax(500px, 820px) minmax(200px, 400px);
  gap: 0 20px;
  width: 100vw;
  background-color: ${({ theme }) => theme.palette.white};
  justify-content: center;
  padding: 40px;
  margin-top: 20px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    margin: 10px;
  }

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.DESKTOP})`} {
    grid-template-columns: minmax(320px, 760px);
    grid-template-rows: 158px;
    gap: 0;
    margin: 0;
    padding: 0;
  }
`;

export const SummarySkeleton = styled.div`
  background-color: ${({ theme }) => theme.palette.white};
  padding: 70px 0 40px 40px;
  border-radius: 2px;
  box-shadow: 0 10px 20px 0 ${({ theme }) => theme.palette.veryLightBlack};
  z-index: 2;
  top: -80px;
  position: relative;
  margin-bottom: -60px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.DESKTOP})`} {
    padding: 15px;
    top: 0;
    margin-bottom: 20px;
  }
`;

export const OpportunitySkeleton = styled.div`
  background-color: ${({ theme }) => theme.palette.white};
  padding: 48px 40px;
  border-radius: 2px;
  box-shadow: 0 10px 20px 0 ${({ theme }) => theme.palette.veryLightBlack};

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.DESKTOP})`} {
    padding: 60px 15px;
    display: flex;
    justify-content: center;
  }
`;

export const OpportunitySkeletonContent = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(100px, 261px));
  gap: 0 20px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.DESKTOP})`} {
    grid-template-columns: repeat(2, 137px);
    gap: 20px 15px;
  }
`;

export const AmenitySkeleton = styled.div`
  background-color: ${({ theme }) => theme.palette.white};
  padding: 48px 40px;
  border-radius: 2px;
  box-shadow: 0 10px 20px 0 ${({ theme }) => theme.palette.veryLightBlack};

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.DESKTOP})`} {
    padding: 60px 15px;
    display: flex;
    justify-content: center;
  }
`;

export const AmenitySkeletonContent = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(60px, 158px));
  gap: 0 30px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.DESKTOP})`} {
    grid-template-columns: repeat(2, 132px);
    gap: 30px 25px;
  }
`;

export const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;

export const SkeletonContentWrapper = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    padding: 0 20px;
  }

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.DESKTOP})`} {
    padding: 0;
  }
`;

const MultipleImageViewSkeleton = () => {
  const { md } = useBreakpoint();

  return (
    <ImagesSkeleton>
      <Skeleton height={md ? 402 : 158} borderRadius={2} />

      {md ? (
        <FlexibleDiv>
          <FlexibleDiv bottom={20}>
            <Skeleton height={192} borderRadius={2} />
          </FlexibleDiv>
          <Skeleton height={192} borderRadius={2} />
        </FlexibleDiv>
      ) : null}
    </ImagesSkeleton>
  );
};

const DetailSummarySkeleton = () => {
  const { xl, md, xs } = useBreakpoint();

  return (
    <SummarySkeleton>
      <Row>
        <Col>
          <FlexibleDiv right={md ? 108 : 0}>
            <Row>
              <Col>
                <FlexibleDiv bottom={md ? 8 : 25} right={md ? 0 : 30}>
                  <Skeleton
                    width={md ? 226 : 236}
                    height={24}
                    borderRadius={4}
                  />
                </FlexibleDiv>
              </Col>
              {!md ? <Skeleton width={24} height={25} /> : null}
            </Row>
            {!md ? (
              <FlexibleDiv bottom={25}>
                <Skeleton
                  width={xs ? 278 : 470}
                  height={140}
                  borderRadius={4}
                />
              </FlexibleDiv>
            ) : null}

            {md ? (
              <FlexibleDiv bottom={52}>
                <Skeleton width={105} height={18} borderRadius={4} />
              </FlexibleDiv>
            ) : null}

            <FlexibleDiv bottom={md ? 35 : 50}>
              {md ? (
                <Row>
                  {[1, 2, 3].map((item) => (
                    <FlexibleDiv key={item} right={9}>
                      <Skeleton width={134} height={40} borderRadius={4} />
                    </FlexibleDiv>
                  ))}
                </Row>
              ) : (
                <Row>
                  <Skeleton width={278} height={44} borderRadius={4} />
                </Row>
              )}
            </FlexibleDiv>

            <FlexibleDiv bottom={8}>
              <Skeleton
                width={xl ? 729 : md ? 400 : xs ? 278 : 550}
                height={18}
                borderRadius={4}
              />
            </FlexibleDiv>

            <FlexibleDiv bottom={8}>
              <Skeleton
                width={xl ? 729 : md ? 400 : xs ? 278 : 550}
                height={18}
                borderRadius={4}
              />
            </FlexibleDiv>

            <Skeleton
              width={xl ? 436 : md ? 320 : xs ? 166 : 360}
              height={18}
              borderRadius={4}
            />
          </FlexibleDiv>
        </Col>
        {md ? (
          <FlexibleDiv top={-20}>
            <Skeleton width={1} height={283} />
          </FlexibleDiv>
        ) : null}
      </Row>
    </SummarySkeleton>
  );
};

const DetailOpportunitySkeleton = () => {
  const { md } = useBreakpoint();
  return (
    <OpportunitySkeleton>
      {md ? (
        <FlexibleDiv bottom={38}>
          <Skeleton width={226} height={24} borderRadius={4} />
        </FlexibleDiv>
      ) : null}
      <OpportunitySkeletonContent>
        {[1, 2, 3, 4].map((item) => (
          <FlexibleDiv key={item}>
            <Skeleton height={md ? 152 : 139} borderRadius={4} />
          </FlexibleDiv>
        ))}
      </OpportunitySkeletonContent>
    </OpportunitySkeleton>
  );
};

const DetailAmenitySkeleton = () => {
  const { md } = useBreakpoint();
  return (
    <AmenitySkeleton>
      {md ? (
        <FlexibleDiv bottom={38}>
          <Skeleton width={226} height={24} borderRadius={4} />
        </FlexibleDiv>
      ) : null}
      <AmenitySkeletonContent>
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <FlexibleDiv key={item}>
            <Skeleton height={md ? 119 : 139} borderRadius={4} />
          </FlexibleDiv>
        ))}
      </AmenitySkeletonContent>
    </AmenitySkeleton>
  );
};

const TourDetailSkeleton = () => {
  return (
    <SkeletonWrapper>
      <MultipleImageViewSkeleton />
      <SkeletonContentWrapper>
        <DetailSummarySkeleton />
        <FlexibleDiv bottom={20}>
          <DetailOpportunitySkeleton />
        </FlexibleDiv>
        <DetailAmenitySkeleton />
      </SkeletonContentWrapper>
    </SkeletonWrapper>
  );
};

export default TourDetailSkeleton;
